import React, { useCallback } from 'react'
import labraLogo from '../../../images/new_labra_logo_compact.svg'
import {
  useLabraChatDispatch,
  useLabraChatSelector,
} from '../../../modules/labraChat/context'
import {
  closeLabraChat,
  closeLabraChatHistory,
  openLabraChatHistory,
  setLabraChatLayout,
  startNewLabraChat,
} from '../../../modules/labraChat/actions'
import { selectIsLabraChatHistoryOpen } from '../../../modules/labraChat/selectors'
import { LabraChatLayout } from '../../../modules/labraChat/reducer'
import { useStyles } from './ChatHeader.styles'
import { ChatHeaderControls } from '../ChatHeaderControls/ChatHeaderControls'
import { ChatMenu } from '../ChatMenu/ChatMenu'

interface ChatHeaderProps {
  /** Product-facing agent name shown in the title bar. */
  title?: string
  /** When true, hides the layout control (e.g. Salesforce Canvas). */
  hideFullScreenToggle?: boolean
  layout: LabraChatLayout
  /** Whether a sidebar column exists for this host. */
  canDock: boolean
  onAgentActivities?: () => void
}

/**
 * The chat panel's title bar: branding + agent name + the window controls
 * (new chat / history / full-screen / close). Product-specific content (funding
 * stat cards, quick actions, todos) is supplied via the panel's `header` slot,
 * not baked in here.
 */
export const ChatHeader: React.FC<ChatHeaderProps> = ({
  title = 'Labra AI',
  hideFullScreenToggle = false,
  layout,
  canDock,
  onAgentActivities,
}) => {
  const { classes } = useStyles()
  const dispatch = useLabraChatDispatch()

  const isHistoryOpen = useLabraChatSelector(selectIsLabraChatHistoryOpen)

  const handleClose = useCallback(() => {
    dispatch(closeLabraChat())
  }, [dispatch])

  const handleNewChat = useCallback(() => {
    dispatch(startNewLabraChat())
  }, [dispatch])

  const handleToggleHistory = useCallback(() => {
    dispatch(isHistoryOpen ? closeLabraChatHistory() : openLabraChatHistory())
  }, [dispatch, isHistoryOpen])

  const handleLayoutChange = useCallback(
    (next: LabraChatLayout) => {
      dispatch(setLabraChatLayout(next))
    },
    [dispatch]
  )

  return (
    <div className={classes.root}>
      <div className={classes.leftGroup}>
        <ChatMenu onAgentActivities={onAgentActivities} />
        <div className={classes.branding}>
          <img src={labraLogo} alt="" className={classes.brandMark} />
          <div className={classes.titleBlock}>
            <span className={classes.title}>{title}</span>
            {isHistoryOpen && (
              <span className={classes.subtitle}>Chat history</span>
            )}
          </div>
        </div>
      </div>

      <ChatHeaderControls
        isHistoryOpen={isHistoryOpen}
        onNewChat={handleNewChat}
        onToggleHistory={handleToggleHistory}
        layout={layout}
        canDock={canDock}
        onLayoutChange={handleLayoutChange}
        onClose={handleClose}
        hideFullScreenToggle={hideFullScreenToggle}
      />
    </div>
  )
}
